'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTheme } from 'styled-components';
import { Trash } from '../Icons';
import { DropzoneProvider, useDropzone as useLMFDropzone } from './DropzoneProvider.client';
import { Container, DeleteIcon, ErrorText, Errors, FilePreview, PreviewContainer, TextContainer, UploadedFileName, } from './styles';
const Dropzone = ({ errorStrings, fileTypes = {
    'image/png': ['.png'],
    'image/jpg': ['.jpg', '.jpeg'],
    'image/eps': ['.eps'],
}, maxFileSize = 3145728, maxFileNum = 1, onFilesAdded, children, ...rest }) => {
    const { setErrors, files, setFiles, borderColor, setBorderColor, colors } = useLMFDropzone();
    const { successBorder, errorBorder, focusBorder, defaultBorder } = colors;
    const { fileInvalidType, fileTooLarge, fileTooSmall, tooManyFiles, defaultError } = errorStrings;
    const ERROR_TEXT_MAP = useMemo(() => {
        return {
            'file-invalid-type': fileInvalidType,
            'file-too-large': fileTooLarge,
            'file-too-small': fileTooSmall,
            'too-many-files': tooManyFiles,
        };
    }, [fileInvalidType, fileTooLarge, fileTooSmall, tooManyFiles]);
    const { isFocused, getRootProps, getInputProps } = useDropzone({
        accept: fileTypes,
        maxFiles: maxFileNum,
        maxSize: maxFileSize,
        onDrop: (acceptedFiles, rejections) => {
            if (files?.length > 0) {
                return;
            }
            if (rejections?.length > 0) {
                handleFlash(errorBorder);
                const groupedErrors = rejections.reduce((acc, r) => {
                    const distinctCodes = Array.from(new Set(r.errors.map(e => e.code)));
                    distinctCodes.forEach(code => {
                        const message = ERROR_TEXT_MAP[code] ?? defaultError;
                        const prev = acc[code] ?? { message, files: [] };
                        acc[code] = {
                            message,
                            files: [...prev.files, r.file.name],
                        };
                    });
                    return acc;
                }, {});
                const errors = Object.entries(groupedErrors).map(([code, { message, files }]) => `${message}${code !== 'too-many-files' ? `: ${files.join(', ')}` : ''}`);
                setErrors(errors);
            }
            else if (acceptedFiles.length > 0) {
                handleFlash(successBorder);
                if (onFilesAdded) {
                    onFilesAdded(acceptedFiles);
                }
                setFiles(acceptedFiles.map(file => Object.assign(file, {
                    preview: URL.createObjectURL(file),
                })));
                setErrors([]);
            }
        },
        onDragOver: () => {
            if (files?.length === 0) {
                setBorderColor(focusBorder);
            }
        },
        onDragEnter: () => {
            if (files?.length === 0) {
                setBorderColor(focusBorder);
            }
        },
        onDragLeave: () => {
            if (files?.length === 0) {
                setBorderColor(defaultBorder);
            }
        },
        onError: (err) => {
            console.error(err);
            setErrors([defaultError]);
        },
    });
    const handleFlash = useCallback((color) => {
        setBorderColor(color);
        setTimeout(() => setBorderColor(defaultBorder), 2000);
    }, [setBorderColor, defaultBorder]);
    useEffect(() => {
        if (isFocused && files?.length === 0) {
            handleFlash(focusBorder);
        }
    }, [isFocused, files, focusBorder, handleFlash]);
    useEffect(() => {
        return () => files.forEach(file => URL.revokeObjectURL(file.preview));
    }, [files]);
    return (_jsx(Container, { ...getRootProps(), tabIndex: 0, "$hasFile": files.length > 0, "$borderColor": borderColor, ...rest, children: _jsxs(_Fragment, { children: [_jsx("input", { ...getInputProps(), style: { display: 'none' }, disabled: files?.length > 0 }), children] }) }));
};
const DropzoneFileNames = (props) => {
    const { files } = useLMFDropzone();
    return (_jsx("div", { ...props, children: files.map(file => (_jsx(UploadedFileName, { children: file?.name }, file?.name + file?.size))) }));
};
const DropzoneTextContainer = ({ children, ...rest }) => {
    const { files } = useLMFDropzone();
    return _jsx(TextContainer, { ...rest, children: files?.length === 0 && _jsx(_Fragment, { children: children }) });
};
const DropzoneErrors = (props) => {
    const { errors } = useLMFDropzone();
    return (_jsx(Errors, { ...props, children: errors.map(e => (_jsx(ErrorText, { title: e, children: e }, e))) }));
};
const DropzonePreviews = ({ onRemoveFiles, ...rest }) => {
    const { color } = useTheme();
    const { files, setFiles } = useLMFDropzone();
    const onDelete = () => {
        setFiles([]);
        if (onRemoveFiles) {
            onRemoveFiles();
        }
    };
    return (_jsx(PreviewContainer, { "$numFiles": files?.length, ...rest, children: files?.length > 0 && (_jsxs(_Fragment, { children: [files.map(file => (_jsx(FilePreview, { src: file.preview ?? '', onLoad: () => {
                        URL.revokeObjectURL(file.preview ?? '');
                    }, alt: `${file.name} upload preview` }, file.name + file.size))), _jsx(DeleteIcon, { onClick: onDelete, type: 'button', children: _jsx(Trash, { stroke: color.stroke.neutral.text.primary.on.background, height: '16px', width: '16px' }) })] })) }));
};
export { Dropzone, DropzoneFileNames, DropzoneErrors, DropzonePreviews, DropzoneTextContainer, useDropzone, DropzoneProvider, };
//# sourceMappingURL=index.js.map